import GiftBoxApi from '@apis/gift/GiftBoxApi';
import giftBoxQueries from '@apis/queries/giftBoxQueries';
import { ErrorResponse } from '@models/error';
import { useActivity, useFlow } from '@stackflow/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

function useDeleteGiftCoupon(giftCouponId?: string) {
    const { t } = useTranslation('gift');
    const { pop } = useFlow();
    const { isActive } = useActivity();
    const canNavigate = useRef(isActive);

    useLayoutEffect(() => {
        canNavigate.current = isActive;
        return () => {
            canNavigate.current = false;
        };
    }, [isActive]);
    const queryClient = useQueryClient();
    const { mutate, isPending } = useMutation({
        mutationFn: () => {
            if (!giftCouponId) throw new Error('missing giftCouponId');
            return GiftBoxApi.deleteGiftCoupon(giftCouponId);
        },
        onSuccess: async () => {
            if (canNavigate.current) pop(2);
            await queryClient.invalidateQueries({ queryKey: giftBoxQueries._def });
            toast.success(t('giftReceived.delete.success'));
        },
        onError: (e: AxiosError<ErrorResponse>) => {
            toast.error(e.response?.data?.message || t('giftReceived.delete.failed'));
        },
    });

    const handleDelete = () => {
        if (!giftCouponId || isPending) return;
        mutate();
    };

    return { handleDelete, isPending };
}

export default useDeleteGiftCoupon;
